import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, take } from 'rxjs';

import { AppointmentRoutingStateService } from '@app/appointment/appointment-routing-state-service/appointment-routing-state.service';
import { AppointmentRoutingStepName } from '@app/appointment/appointment-routing-state-service/appointment-routing-steps';
import {
  ManagePaymentMethodsQueryParams,
  ManagePaymentMethodsSource,
} from '@app/appointment/manage-payment-methods/manage-payment-methods.component';
import { AnalyticEvent } from '@app/core/analytics.service';
import { FeatureFlagVariants } from '@app/core/feature-flags/feature-flags';
import { LinksService } from '@app/core/links.service';
import { FLOW_APPOINTMENT_BOOKING } from '@app/core/mixpanel.constants';
import {
  InsuranceCaptureService,
  InsuranceData,
  InsuranceStatus,
} from '@app/shared/insurance-capture/insurance-capture.service';
import { PaymentCaptureService, PaymentMethodData } from '@app/shared/payment-capture/payment-capture.service';

import { AppointmentAnalyticsService } from '../appointment-analytics.service';
import { AppointmentBookingStateService } from '../appointment-booking-state-service';
import { InsuranceSubmitEvent } from './add-insurance/add-insurance.component';

export enum InsuranceStep {
  CONFIRM_INSURANCE = 'confirm-insurance',
  CONFIRM_EXISTING_INSURANCE = 'confirm-existing-insurance',
  ADD_INSURANCE = 'add-insurance',
}

export enum ManageInsuranceQueryParams {
  SOURCE = 'source',
  CONFIRMATION_ID = 'confirmationId',
}

export const ManageInsuranceSource = {
  REVIEW_BOOKING: 'review',
  BOOKING_CONFIRMATION: 'confirmation',
  APPOINTMENT_INVENTORY: 'inventory',
  SWITCH_TO_REMOTE: 'switchRemote',
  AMAZON: 'amazon',
} as const;
export type ManageInsuranceSource = typeof ManageInsuranceSource[keyof typeof ManageInsuranceSource];

export const SKIP_COPAY_COLLECTION_APPOINTMENT_TYPE_IDS = [36, 27, 68, 178, 176, 177, 354, 37, 175, 90, 91];

@Component({
  selector: 'om-manage-insurance',
  templateUrl: './manage-insurance.component.html',
  styleUrls: ['./manage-insurance.component.scss'],
})
export class ManageInsuranceComponent implements OnInit {
  InsuranceStep = InsuranceStep;
  currentStep: InsuranceStep;
  protected loading = true;
  insurance: InsuranceData;
  hasExistingNonSelfPayInsurance: boolean;
  // acts as a local state machine to manage navigation state within this component
  historyStack: InsuranceStep[] = [];
  paymentCaptureFlagM3: FeatureFlagVariants;
  paymentCaptureFlagM3Enabled: boolean;
  paymentMethods: PaymentMethodData[];
  source: ManageInsuranceSource;
  confirmationId: string | null;
  showSkip: boolean;
  showBack: boolean;
  copayCollectionRequired: boolean;
  isRemote: boolean;

  constructor(
    private readonly routingStateService: AppointmentRoutingStateService,
    private readonly router: Router,
    private readonly links: LinksService,
    private readonly insuranceCaptureService: InsuranceCaptureService,
    private readonly paymentCaptureService: PaymentCaptureService,
    private readonly analyticsService: AppointmentAnalyticsService,
    private readonly route: ActivatedRoute,
    private readonly appointmentBookingStateService: AppointmentBookingStateService,
  ) {}

  ngOnInit() {
    const bookingState = this.appointmentBookingStateService.getAppointmentBookingState();
    this.copayCollectionRequired = !SKIP_COPAY_COLLECTION_APPOINTMENT_TYPE_IDS.includes(
      bookingState?.appointmentType?.id,
    );
    this.isRemote = bookingState?.appointmentType?.remote === true;

    const params = this.route.snapshot.queryParamMap;
    this.source = params.get(ManageInsuranceQueryParams.SOURCE) as ManageInsuranceSource;
    this.confirmationId = params.get(ManageInsuranceQueryParams.CONFIRMATION_ID);
    this.showSkip = this.isEligibleForInsuranceCaptureEntryPointPage();

    combineLatest([
      this.insuranceCaptureService.getPrimaryInsurance$(),
      this.paymentCaptureService.getPaymentCaptureFlagM3$(),
      this.paymentCaptureService.getPaymentCaptureFlagM3Enabled$(),
      this.paymentCaptureService.getPaymentMethods$(),
    ])
      .pipe(take(1))
      .subscribe(([insurance, paymentCaptureFlagM3, paymentCaptureFlagM3Enabled, paymentMethods]) => {
        this.insurance = insurance;
        this.hasExistingNonSelfPayInsurance = insurance.insuranceStatus !== InsuranceStatus.SELF_PAY;
        this.paymentCaptureFlagM3 = paymentCaptureFlagM3;
        this.paymentCaptureFlagM3Enabled = paymentCaptureFlagM3Enabled;
        this.paymentMethods = paymentMethods;
        this.loading = false;

        if (!this.currentStep) {
          this.goToInitialStep();
        }
      });
  }

  isSourceReviewBooking(): boolean {
    return this.source === ManageInsuranceSource.REVIEW_BOOKING;
  }

  isSourceBookingConfirmation(): boolean {
    return this.source === ManageInsuranceSource.BOOKING_CONFIRMATION;
  }

  isSourceAppointmentInventory(): boolean {
    return this.source === ManageInsuranceSource.APPOINTMENT_INVENTORY;
  }

  isSourceSwitchToRemote(): boolean {
    return this.source === ManageInsuranceSource.SWITCH_TO_REMOTE;
  }

  isSourceAmazon(): boolean {
    return this.source === ManageInsuranceSource.AMAZON;
  }

  isEligibleForInsuranceCaptureEntryPointPage(): boolean {
    return this.isSourceAppointmentInventory() || this.isSourceSwitchToRemote() || this.isSourceAmazon();
  }

  goBack() {
    if (this.historyStack.length > 1) {
      // If there's local history, go back within component
      this.historyStack.pop();
      const step = this.historyStack.pop() as InsuranceStep;
      this.goToStep(step);
    } else {
      this.goToSource();
    }
  }

  goToStep(step: InsuranceStep): void {
    this.historyStack.push(step);

    this.currentStep = step;
  }

  goToDestination(event?: InsuranceSubmitEvent) {
    if (this.isSourceBookingConfirmation()) {
      const route = this.confirmationId ? this.links.appointmentConfirmation(this.confirmationId) : this.links.home;
      this.router.navigate([route]);
      return;
    } else if (this.isSourceReviewBooking()) {
      this.router.navigate([this.links.appointmentReview]);
      return;
    } else if (this.isEligibleForInsuranceCaptureEntryPointPage()) {
      this.insuranceCaptureService
        .getPrimaryInsurance$()
        .pipe(take(1))
        .subscribe(insurance => {
          this.insurance = insurance;
          const hasNonZeroCopay = event ? (event.copay ?? 0) > 0 : (this.insurance.copay ?? 0) > 0;
          const hasNoPreferredPaymentMethod = !this.paymentMethods.some(method => method.isPreferredCopay);

          // Navigate to payment capture if copay is required, non-zero, and no preferred payment method exists
          if (this.copayCollectionRequired && hasNonZeroCopay && hasNoPreferredPaymentMethod) {
            this.analyticsService.trackPaymentCaptureM3ExperimentVariationAssigned(this.paymentCaptureFlagM3);
            if (this.paymentCaptureFlagM3Enabled) {
              this.router.navigate([this.links.managePaymentMethods], {
                queryParams: {
                  [ManagePaymentMethodsQueryParams.SOURCE]: ManagePaymentMethodsSource.INSURANCE,
                  [ManagePaymentMethodsQueryParams.IS_NEW_INSURANCE]: event ? 'true' : null,
                  [ManagePaymentMethodsQueryParams.CONFIRMATION_ID]: this.confirmationId,
                },
              });
              return;
            }
          }

          if (this.isSourceAmazon() && !this.confirmationId) {
            this.router.navigate([this.links.home]);
            return;
          }

          this.confirmationId
            ? this.router.navigate([this.links.appointmentConfirmation(this.confirmationId)])
            : this.router.navigate([this.links.appointmentReview]);
        });
    } else {
      this.router.navigate([this.links.home]);
    }
  }

  trackAnalytics(analytics: AnalyticEvent) {
    this.analyticsService.trackEventWithDefaultProperties({ ...analytics, flow: FLOW_APPOINTMENT_BOOKING });
  }

  private goToInitialStep() {
    if (this.isEligibleForInsuranceCaptureEntryPointPage()) {
      this.showBack = !this.isSourceAmazon();

      if (this.hasExistingNonSelfPayInsurance && this.isRemote && this.paymentCaptureFlagM3Enabled) {
        this.goToStep(InsuranceStep.CONFIRM_EXISTING_INSURANCE);
        return;
      }

      // Redirect if coming from Amazon and doesn't meet the entry point criteria for Insurance Capture
      if (
        this.isSourceAmazon() &&
        !(
          this.insurance.insuranceStatus === InsuranceStatus.SELF_PAY ||
          (this.paymentCaptureFlagM3Enabled && this.isRemote)
        )
      ) {
        this.goToDestination();
        return;
      }

      this.goToStep(InsuranceStep.CONFIRM_INSURANCE);
      return;
    }

    this.goToStep(InsuranceStep.ADD_INSURANCE);
  }

  private goToSource() {
    if (this.isSourceAppointmentInventory()) {
      this.routingStateService.navigateToStep(AppointmentRoutingStepName.InventoryResults);
      return;
    }

    const routeMap: Partial<Record<ManageInsuranceSource, string>> = {
      [ManageInsuranceSource.REVIEW_BOOKING]: this.links.appointmentReview,
      [ManageInsuranceSource.BOOKING_CONFIRMATION]: this.confirmationId
        ? this.links.appointmentConfirmation(this.confirmationId)
        : this.links.home,
      [ManageInsuranceSource.SWITCH_TO_REMOTE]: this.links.appointmentRescheduleRemote,
    };

    this.router.navigate([routeMap[this.source] || this.links.home]);
  }
}
